// Apagar dado importado de um jeito que DURA.
//
// O problema, em uma frase: apagar uma linha que veio de integração não
// adianta nada se o próximo sync a traz de volta.
//
// Foi por isso que as duas telas nasceram PROIBINDO apagar dado externo —
// Sleep.tsx (`canDelete: source === 'manual'`) e FitnessHistory.tsx (import
// Strava é read-only). O comentário no próprio código diz o motivo: "sem
// deleted_at, o próximo sync reinsere a linha". A proibição era honesta: era
// melhor não oferecer o botão do que oferecer um botão que mente.
//
// Aqui a proibição vira permissão. O DELETE continua sendo DELETE (a linha
// some de verdade — resposta certa para dado de saúde), e uma lápide em
// `integration_deletions` guarda só a chave natural. Todo sync consulta as
// lápides antes de escrever (ver filterTombstoned em healthMap.ts), então o
// que o usuário apagou fica apagado.
//
// Corolário que vale escrever: a lápide guarda `source` + `external_id` e
// NUNCA o valor. Apagar dado de saúde não pode deixar o dado de saúde para
// trás.

import { supabase } from "@/integrations/supabase/client";
import { isExternalSource } from "@/lib/health/healthMap";

export type TombstonedTable = "sleep_logs" | "workout_sessions" | "health_metrics" | "notes";

// isExternalSource mora em healthMap.ts (módulo sem imports, testável direto
// pelo vitest) e é reexportado aqui para os call sites continuarem importando
// as duas coisas do mesmo lugar.
export { isExternalSource };

/**
 * Apaga a linha E registra a lápide.
 *
 * Ordem importa: a LÁPIDE PRIMEIRO. Se o delete falhar depois, o pior caso é
 * uma lápide para uma linha que continua lá — o sync deixa de atualizá-la e o
 * usuário tenta apagar de novo. Na ordem inversa, uma falha ao gravar a
 * lápide deixaria a linha apagada e ressuscitável: exatamente o bug que este
 * módulo existe para impedir, e silencioso.
 */
export async function deleteImportedRow(params: {
  table: TombstonedTable;
  id: string;
  userId: string;
  source: string | null | undefined;
  externalId: string | null | undefined;
  assertCurrent?: () => void;
  requireConfirmation?: boolean;
}): Promise<void> {
  const { table, id, userId, source, externalId, assertCurrent = () => {}, requireConfirmation = false } = params;
  assertCurrent();

  if (isExternalSource(source) && externalId) {
    // ⚠️ `ignoreDuplicates: true` NÃO é detalhe: sem ele o supabase-js manda
    // `Prefer: resolution=merge-duplicates`, o PostgREST emite
    // `INSERT ... ON CONFLICT DO UPDATE`, e o Postgres avalia a política de
    // UPDATE nesse caminho. `integration_deletions` não TEM política de
    // UPDATE (e não deve ter — lápide é escrita-uma-vez), então RLS recusa
    // com 42501 e a exclusão inteira falha.
    //
    // Isso não é teórico: a segunda exclusão da mesma chave natural bate nele.
    // O caminho certo é DO NOTHING — a lápide já existir é sucesso, não
    // conflito. `.select()` volta vazio nesse caso, e por isso aqui NÃO há
    // checagem de zero linhas (§4.9 vale para escrita que precisa acontecer;
    // esta é idempotente por construção).
    const { error: tombErr } = await (supabase as any)
      .from("integration_deletions")
      .upsert(
        {
          user_id: userId,
          table_name: table,
          source: source,
          external_id: externalId,
        },
        { onConflict: "user_id,table_name,source,external_id", ignoreDuplicates: true },
      )
      .select("id");
    if (tombErr) {
      console.error("[deleteImported] tombstone write failed — aborting delete:", tombErr);
      // Fail-closed: sem lápide, apagar seria temporário. Melhor não apagar e
      // deixar o erro subir para o toast do que fingir que apagou.
      throw tombErr;
    }
  }

  assertCurrent();
  const query = supabase.from(table).delete().eq("id", id).eq("user_id", userId);
  const { data, error } = requireConfirmation ? await query.select("id") : await query;
  assertCurrent();
  if (requireConfirmation && !error && (!Array.isArray(data) || data.length !== 1 || data[0].id !== id)) throw new Error("Delete not confirmed");
  if (error) {
    console.error("[deleteImported] delete failed:", error);
    throw error;
  }
}
